import { cloneDeep } from 'lodash-es';
import { AxiosInstance } from 'axios';
import {
  IBooking,
  IBookingFromPouch,
  IBookingNotificationItem,
  IPouchDBInstance,
  IPouchService,
} from 'shared-types/index';
import AdminService from 'external-services/diary-admin-service/admin.service';
import NetworkConnectionService from 'external-services/network-connection-service/index';
import CustomerOfflineSyncService from 'external-services/customer-offline-sync-service/customerOfflineSync.service';
import UtilsService from 'external-services/utils-service/index';
import PouchLoadService from 'external-services/pouch-load-service/index';
import CustomerLookupService from 'external-services/customer-lookup-service/index';
import { appURLS } from 'appRoot/app.constants';
import NetworkRequestsService from 'external-services/network-requests-service';
import DateUtilsService from 'external-services/date-utils-service';

/**
 * This service is used to notify the backend when bookings are made so it can send emails sms confirmations etc
 */

const NS = 'BookingNotificationsService'; // jshint ignore:line

export interface IBookingNotificationDoc {
  bookingId: string; //"booking_2022-05-20_EHQCT73IBMGNQ_1653011948310",
  venueId: number; //2804
  type: string; //"new"
  notify?: boolean;
  smsNotify?: boolean;
  _id?: string; //"nofication_2804_29TKLTHN9YK80_1653011991075",
  _rev?: string; //"1-06346ba763ea5f21c7dcc22de3772328"
}
const api: AxiosInstance = NetworkRequestsService.getGlobalAPI();

class BookingNotificationsService {
  private pouchService: IPouchService;

  private db: IPouchDBInstance;
  private syncing: boolean = false;

  public NEW: string = 'new';
  public EDIT: string = 'edit';

  public setup(
    pouchService: {
      getNewOfflineBookingsInstance: () => PouchDB.Database;
    }
  ) {
    this.pouchService = pouchService;
    // DB to temporary store notifications generated at offline time
    this.db = this.pouchService.getNewOfflineBookingsInstance();
  }

  public init(): void {
    this.handleNetworkStatusChange();
  }

  // api instance must now be passed into setup function
  // getApiInstance(): AxiosInstance {
  //   if (!this.api) {
  //     this.api = NetworkRequestsService.getNewAPIAxiosInstance(true, {
  //       baseURL: appURLS.API,
  //       headers: {
  //         'Authorization': this.AuthService.authStr
  //       }
  //     });
  //   }
  //
  //   return this.api;
  // }

  private handleNetworkStatusChange() {
    NetworkConnectionService.addDelayedNavigatorOnlineListener((isOnline) => {
      if (isOnline) {
        this.checkForNotifications();
      } else {
        Promise.all([
          AdminService.checkApiState(),
          NetworkConnectionService.checkCouchDbIsReachable(),
        ]).finally(() => {
          const customerOfflineSyncQueueLength = CustomerOfflineSyncService.getSyncQueueLength();

          if (customerOfflineSyncQueueLength > 0) {
            // if there are offline notifications, poll until they are all finished syncing
            const interval = UtilsService.setCustomInterval(
              this.eventForCheckOnlineStatus(this),
              1500,
              20
            );
            interval.start();
          } else {
            this.checkForNotifications();
          }
        });
      }
    }, false);
  }
  public eventForCheckOnlineStatus(that: any) {
    return (stop: Function) => {
      const customerOfflineSyncQueueLength = CustomerOfflineSyncService.getSyncQueueLength();
      if (!customerOfflineSyncQueueLength) {
        that.checkForNotifications();
        stop();
      }
    };
  }

  public async sendNotification(
    originalBooking: IBooking | IBookingFromPouch,
    type: string,
    emailNotify: boolean = false,
    smsNotify: boolean = false,
    ignoreOnFail: boolean = false,
    venueId: number = null,
    modifiedProperties: string = null
  ): Promise<void> {
    /** bookedAt, time is string  for IBooking type */
    venueId = venueId || AdminService.getVenue().id;
    if (!venueId) {
      return;
    }

    // Note: the 'info' object needs to be cloned before the async 'CustomerLookupService.getById'
    // call because to the chance of mutation of the object after 'sendNotification' is called.
    // Happening in Booking Creator on Diary,
    // where the 'time' property gets changed to a date object instead of a string,
    // causing the api request to fail back end validation.
    
    const info: IBookingNotificationItem = cloneDeep(
      originalBooking
    ) as unknown as IBookingNotificationItem;

    let theCustomer = null;
    if (originalBooking.customerId) {
      theCustomer = await CustomerLookupService.getById(originalBooking.customerId);
    }

    // Integrate customer and notification into the notification data
    if (theCustomer) {
      delete info.customerId;
      info.customer = theCustomer;
    }
    info.notify = emailNotify;
    info.smsNotify = smsNotify;
    info.modifiedProperties = modifiedProperties;

    const axiosInstance = type === this.EDIT ? api.patch : api.post;

    axiosInstance(appURLS.API + '/restaurants/venues/' + venueId + '/bookings', info)
      .then(() => {
        console.log('Booking created/edited notifcation sent');
        this.checkForNotifications();
      })
      .catch((err: any) => {
        if (info && ignoreOnFail !== true) {
          // On API fail, store the notification localy
          this.saveNotification(info._id, type, emailNotify, venueId);
        }
        console.log('Failed to send notifications', err);
      });
  }

  /************************************* PRIVATE *****************************************/

  public saveNotification(bookingId: string, type: string, notify: boolean, venueId: number) {
    const doc = {
      _id: PouchLoadService.getNewId(['nofication', venueId.toString()]),
      bookingId,
      venueId,
      type,
      notify,
    };
    // save the booking so the reqest can be made at a later date.
    this.db.put(doc).catch((err: any) => {
      console.error('err', err);
    });
  }

  /**
   * Check to database for a saved notification and tries to save it and loop until there are none left
   */

  private checkForNotifications(loop?: boolean): void {
    // stop the process been kicked of more then once at a time
    if (!this.syncing || loop) {
      this.syncing = true;
      this.getNotification().then((notification: IBookingNotificationDoc) => {
        if (notification && PouchLoadService.getDbLocal()) {
          return PouchLoadService.getDbLocal()
            .get(notification.bookingId)
            .then((booking: unknown) => {
              // try and send the booking
              const type = notification.type || this.NEW;
              return this.sendNotification(
                booking as IBookingNotificationItem,
                type,
                notification.notify,
                notification.smsNotify,
                true,
                notification.venueId
              );
            })
            .then(() => {
              // the notification has been sent to the server delete if from the datatbase
              return this.db.remove(notification);
            })
            .then(() => {
              // the item has been deleted call again to check if their other notification
              this.checkForNotifications(true);
            })
            .catch((err: any) => {
              if (err.reason && err.reason === 'deleted') {
                // the booking was deleted
                return this.db.remove(notification);
              }
              this.syncing = false;
            });
        } else {
          this.syncing = false;
          return null;
        }
      });
    }
  }

  /**
   * Get a saved notification if there is one
   */

  private getNotification(): Promise<IBookingNotificationDoc> {
    let venueId = 'na';
    const venue = AdminService.getVenue();
    if (venue) {
      venueId = venue.id.toString();
    }
    return this.db
      .allDocs({
        include_docs: true,
        attachments: true,
        startkey: 'nofication_' + venueId,
        endkey: 'nofication_' + venueId + '\uffff',
        limit: 1,
      })
      .then(
        (notifications: {
          total_rows: number;
          offset: number;
          rows: {
            id: string;
            key: string;
            value: {
              rev: string;
            };
            doc: IBookingNotificationDoc;
          }[];
        }) => {
          return notifications.rows.length ? notifications.rows[0].doc : null;
        }
      );
  }
}

const instance = new BookingNotificationsService();
export default instance as BookingNotificationsService;
